/**
 * Iconography and nostalgia: screensaver classics and rave culture, all drawn from scratch
 * (no third-party logos or characters).
 */
import type { VisualizationDef } from '../../../shared/viz';

const SHAPES = /* glsl */ `
float segDist(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
// 0..1 triangle wave: bounces between the walls.
float tri(float x) { return 1.0 - abs(mod(x, 2.0) - 1.0); }
`;

export const bouncingLogo: VisualizationDef = {
  id: 'bouncing-logo',
  name: 'Bouncing Logo',
  description:
    'The screensaver classic: a logo drifting corner to corner, changing colour every time it hits a wall. A corner hit sets off a celebration.',
  tags: ['energy:low', 'style:retro'],
  role: 'element',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0.1, max: 2, default: 0.6 },
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 2.5, default: 1 },
    { id: 'pulse', label: 'Beat pulse', type: 'float', min: 0, max: 1, default: 0.3 },
  ],
  fragment: /* glsl */ `
${SHAPES}
// Our own "VIZ" badge: a rounded frame with the letters drawn as strokes.
float logo(vec2 q) {
  vec2 b = abs(q) - vec2(0.82, 0.38);
  float frame = abs(length(max(b, 0.0)) + min(max(b.x, b.y), 0.0) - 0.08);
  float d = frame;
  d = min(d, segDist(q, vec2(-0.62, 0.22), vec2(-0.45, -0.22)));
  d = min(d, segDist(q, vec2(-0.45, -0.22), vec2(-0.28, 0.22)));
  d = min(d, segDist(q, vec2(-0.05, 0.22), vec2(-0.05, -0.22)));
  d = min(d, segDist(q, vec2(0.18, 0.22), vec2(0.58, 0.22)));
  d = min(d, segDist(q, vec2(0.58, 0.22), vec2(0.18, -0.22)));
  d = min(d, segDist(q, vec2(0.18, -0.22), vec2(0.58, -0.22)));
  return d;
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float s = 0.17 * uP[1] * (1.0 + 0.15 * pulse() * uP[2]);
  vec2 half_ = vec2(0.9, 0.46) * s;
  float t = uTime * uP[0];
  vec2 travel = vec2(0.31, 0.23) * t / vec2(aspect - 2.0 * half_.x, 1.0 - 2.0 * half_.y);
  vec2 pos = vec2(-aspect * 0.5 + half_.x, -0.5 + half_.y) + vec2(tri(travel.x), tri(travel.y)) * vec2(aspect - 2.0 * half_.x, 1.0 - 2.0 * half_.y);
  // Each wall hit picks a new colour.
  float hits = floor(travel.x) + floor(travel.y);
  vec3 c = palette(hash21(vec2(hits, 3.0)));
  float d = logo((p - pos) / s) * s;
  vec3 col = c * (smoothstep(0.006, 0.002, d) + 0.25 * exp(-d * 60.0));
  // Corner hit: both walls within a moment of each other → a burst of sparkles.
  vec2 fr = abs(fract(travel + 0.5) - 0.5);
  float corner = smoothstep(0.04, 0.0, max(fr.x, fr.y));
  if (corner > 0.0) {
    for (int i = 0; i < 24; i++) {
      float fi = float(i);
      float a = fi / 24.0 * TAU;
      vec2 sp = pos + vec2(cos(a), sin(a)) * (0.05 + 0.4 * (1.0 - corner));
      col += palette(fi / 24.0) * exp(-length(p - sp) * 120.0) * corner * 2.0;
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const morphingSolid: VisualizationDef = {
  id: 'morphing-solid',
  name: 'Morphing Solid',
  description:
    'A chrome shape bouncing around the screen, melting from sphere to cube to torus to octahedron every bar, like the old 3D screensavers.',
  tags: ['energy:mid', 'style:retro'],
  role: 'element',
  params: [
    { id: 'speed', label: 'Bounce speed', type: 'float', min: 0, max: 2, default: 0.5 },
    { id: 'morph', label: 'Bars per shape', type: 'float', min: 0.5, max: 8, default: 1 },
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 2, default: 1 },
    { id: 'chrome', label: 'Chrome', type: 'float', min: 0, max: 1, default: 0.7 },
  ],
  fragment: /* glsl */ `
${SHAPES}
mat3 rotXY(float a, float b) {
  float ca = cos(a), sa = sin(a), cb = cos(b), sb = sin(b);
  return mat3(cb, 0.0, -sb, 0.0, 1.0, 0.0, sb, 0.0, cb) * mat3(1.0, 0.0, 0.0, 0.0, ca, sa, 0.0, -sa, ca);
}
float shape(vec3 p, float i) {
  if (i < 0.5) return length(p) - 1.0;
  if (i < 1.5) { vec3 q = abs(p) - vec3(0.72); return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0) - 0.12; }
  if (i < 2.5) { vec2 q = vec2(length(p.xz) - 0.85, p.y); return length(q) - 0.33; }
  vec3 q = abs(p); return (q.x + q.y + q.z - 1.25) * 0.57735;
}
float scene(vec3 p) {
  float m = uBeats / (4.0 * uP[1]);
  float i = mod(floor(m), 4.0);
  float k = smoothstep(0.55, 1.0, fract(m));
  return mix(shape(p, i), shape(p, mod(i + 1.0, 4.0)), k);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float s = 0.22 * uP[2] * (1.0 + 0.1 * uBass);
  float t = uTime * uP[0];
  vec2 c = vec2((tri(t * 0.23) - 0.5) * (aspect - 2.0 * s), (tri(t * 0.17 + 0.3) - 0.5) * (1.0 - 2.0 * s));
  vec2 q = (p - c) / s;
  vec3 col = vec3(0.0);
  if (length(q) < 1.8) {
    vec3 ro = vec3(q * 1.0, -3.0);
    vec3 rd = vec3(0.0, 0.0, 1.0);
    mat3 R = rotXY(uTime * 0.6, uTime * 0.45);
    float d = 0.0, tt = 0.0;
    for (int i = 0; i < 48; i++) {
      d = scene(R * (ro + rd * tt));
      if (d < 0.002 || tt > 6.0) break;
      tt += d;
    }
    if (d < 0.01) {
      vec3 pos = R * (ro + rd * tt);
      vec2 e = vec2(0.002, 0.0);
      vec3 n = normalize(vec3(scene(pos + e.xyy) - scene(pos - e.xyy), scene(pos + e.yxy) - scene(pos - e.yxy), scene(pos + e.yyx) - scene(pos - e.yyx)));
      n = transpose(R) * n;
      vec3 l = normalize(vec3(-0.5, 0.7, -0.6));
      float diff = max(dot(n, l), 0.0);
      vec3 r = reflect(rd, n);
      vec3 env = mix(palette(0.15) * 0.45 + 0.05, palette(0.7 + 0.3 * r.y) + 0.15, smoothstep(-0.4, 0.5, r.y)) + vec3(1.0) * exp(-abs(r.y - 0.25) * 10.0) * 0.8;
      vec3 base = palette(0.55) * (0.3 + 0.9 * diff);
      float spec = pow(max(dot(r, l), 0.0), 40.0);
      col = mix(base, env, uP[3]) + vec3(spec) * (0.8 + pulse());
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const trippyMaze: VisualizationDef = {
  id: 'trippy-maze',
  name: 'Trippy Maze',
  description:
    'Walking an endless maze, like the old 3D screensaver, but the walls breathe colour, ripple with the spectrum and the corridor wobbles on the beat.',
  tags: ['energy:mid', 'style:retro'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Walk speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'trip', label: 'Trippiness', type: 'float', min: 0, max: 1, default: 0.6 },
    { id: 'density', label: 'Wall density', type: 'float', min: 0.2, max: 0.9, default: 0.6 },
    { id: 'fov', label: 'Field of view', type: 'float', min: 0.5, max: 1.6, default: 1 },
  ],
  fragment: /* glsl */ `
const float L = 4.0;
// The route: a staircase of corridors, L cells along x, then L along z, and so on.
vec2 routeAt(float s) {
  float k = floor(s / L), m = s - k * L;
  float h = floor(k * 0.5);
  return mod(k, 2.0) < 0.5 ? vec2(h * L + m, h * L) : vec2((h + 1.0) * L, h * L + m);
}
bool onRoute(vec2 c) {
  bool alongX = mod(c.y, L) < 0.5 && c.x >= c.y && c.x <= c.y + L;
  bool alongZ = mod(c.x, L) < 0.5 && c.y >= c.x - L && c.y <= c.x;
  return alongX || alongZ;
}
bool isWall(vec2 c) {
  if (onRoute(c)) return false;
  return hash21(c + 0.5) < uP[2];
}
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float trip = uP[1];
  // Beat wobble of the view.
  uv += trip * 0.02 * vec2(sin(uv.y * 10.0 + uTime * 2.0), cos(uv.x * 8.0 + uTime * 1.7)) * (0.5 + pulse());
  float s = uTime * 0.8 * uP[0];
  float k = floor(s / L), m = s - k * L;
  vec2 cam = routeAt(s) + 0.5;
  float a0 = mod(k, 2.0) * 1.5707963;
  float turn = mod(k, 2.0) < 0.5 ? 1.5707963 : -1.5707963;
  float heading = a0 + turn * smoothstep(L - 0.7, L, m);
  float ang = heading - uv.x * uP[3];
  vec2 rd = vec2(cos(ang), sin(ang));
  // DDA through the grid until a wall.
  vec2 cell = floor(cam);
  vec2 stepv = sign(rd);
  vec2 tDelta = abs(1.0 / rd);
  vec2 tMax = (stepv * (cell - cam) + stepv * 0.5 + 0.5) * tDelta;
  float dist = 30.0;
  float side = 0.0;
  for (int i = 0; i < 40; i++) {
    if (tMax.x < tMax.y) { dist = tMax.x; tMax.x += tDelta.x; cell.x += stepv.x; side = 0.0; }
    else { dist = tMax.y; tMax.y += tDelta.y; cell.y += stepv.y; side = 1.0; }
    if (isWall(cell)) break;
  }
  float perp = dist * cos(uv.x * uP[3]);
  float wallH = 0.42 / max(perp, 0.05);
  vec3 col;
  float fog = exp(-perp * 0.18);
  if (abs(uv.y) < wallH) {
    vec2 hit = cam + rd * dist;
    float u = fract(side < 0.5 ? hit.y : hit.x);
    float v = uv.y / wallH * 0.5 + 0.5;
    float swirl = sin((u + v) * 12.0 + uTime * 2.0 + perp) * trip;
    vec3 wall = palette(fract(u * 0.5 + v * 0.3 + perp * 0.05 + uTime * 0.05 * trip) + swirl * 0.1);
    // Spectrum bars climbing the walls.
    float bar = step(v, spectrum(u * 0.9) * 1.2) * step(0.1, fract(u * 8.0)) * step(fract(u * 8.0), 0.9);
    wall = mix(wall * 0.55, wall * 1.2 + 0.1, bar * trip);
    col = wall * (side > 0.5 ? 0.8 : 1.0) * fog * (0.8 + 0.4 * pulse());
  } else {
    // Floor and ceiling: a checkerboard that swims when it's trippy.
    float rowDist = 0.42 / abs(uv.y);
    vec2 w = cam + rd * rowDist / cos(uv.x * uP[3]);
    w += trip * 0.2 * vec2(sin(w.y * 2.0 + uTime), cos(w.x * 2.0 - uTime));
    float check = mod(floor(w.x * 2.0) + floor(w.y * 2.0), 2.0);
    col = palette(uv.y > 0.0 ? 0.8 : 0.2) * (0.15 + 0.35 * check) * exp(-rowDist * 0.18);
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const meltingSmiley: VisualizationDef = {
  id: 'melting-smiley',
  name: 'Melting Smiley',
  description:
    'The acid-house smiley, melting and dripping over each phrase, wobbling on the bass, then pulling itself back together.',
  tags: ['energy:high', 'style:rave'],
  role: 'element',
  params: [
    { id: 'melt', label: 'Melt', type: 'float', min: 0, max: 1.5, default: 0.8 },
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 2, default: 1 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0 },
    { id: 'rings', label: 'Trippy rings', type: 'float', min: 0, max: 1, default: 0 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y / uP[1];
  // Melt over 16 bars, re-form at the start; the bass wobbles it. (uBeats keeps running even
  // before a tempo is found.)
  float phrase = fract(uBeats / 64.0);
  float M = uP[0] * smoothstep(0.1, 0.9, phrase) * (1.0 - smoothstep(0.92, 1.0, phrase));
  vec2 q = p;
  q.x += sin(q.y * 9.0 + uTime * 2.0) * 0.012 * (M + uBass);
  // Drips: points lower down sample from higher up, by an uneven amount per column.
  float drip = (0.15 + 0.85 * fbm(vec2(q.x * 7.0, 1.3))) * smoothstep(0.35, -0.4, q.y);
  q.y += M * drip * 0.45;
  float R = 0.3;
  float face = length(q) - R;
  vec3 yellow = mix(vec3(1.0, 0.86, 0.1), palette(0.6), uP[2]);
  vec3 col = vec3(0.0);
  if (uP[3] > 0.0) {
    float r = length(p);
    col += palette(fract(r * 3.0 - uTime * 0.3)) * 0.35 * uP[3] * smoothstep(0.0, 0.6, r);
  }
  if (face < 0.0) {
    col = yellow * (0.85 + 0.15 * smoothstep(-R, 0.0, -length(q - vec2(-0.1, 0.12))));
    // Eyes and smile.
    vec2 e = vec2(abs(q.x) - 0.105, q.y - 0.07);
    float eye = length(e / vec2(0.035, 0.075)) - 1.0;
    float smileR = length(q - vec2(0.0, 0.03));
    float smile = abs(smileR - 0.19) - 0.022;
    smile = max(smile, q.y - 0.0);
    float ink = min(eye * 0.035, smile);
    col = mix(col, vec3(0.03), smoothstep(0.004, 0.0, ink));
    col *= 0.9 + 0.25 * pulse();
  }
  col = mix(col, vec3(0.04), smoothstep(0.006, 0.0, abs(face)) * 0.8);
  fragColor = vec4(col, 1.0);
}`,
};

export const mystify: VisualizationDef = {
  id: 'mystify',
  name: 'Mystify',
  description:
    'Two bouncing polygons trailing echoes of themselves, after the classic screensaver; they flare on the beat.',
  tags: ['energy:mid', 'style:retro'],
  role: 'element',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0.1, max: 3, default: 1 },
    { id: 'echoes', label: 'Echoes', type: 'float', min: 1, max: 10, default: 6, step: 1 },
    { id: 'spacing', label: 'Echo spacing', type: 'float', min: 0.02, max: 0.3, default: 0.1 },
  ],
  fragment: /* glsl */ `
${SHAPES}
vec2 vertex(float poly, float v, float t, float aspect) {
  vec2 speeds = vec2(0.13 + 0.07 * hash21(vec2(poly, v)), 0.11 + 0.08 * hash21(vec2(v, poly + 4.0)));
  vec2 ph = vec2(hash21(vec2(poly + 7.0, v)), hash21(vec2(v + 3.0, poly)));
  return (vec2(tri(t * speeds.x + ph.x * 2.0), tri(t * speeds.y + ph.y * 2.0)) - 0.5) * vec2(aspect - 0.1, 0.9);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  vec3 col = vec3(0.0);
  float n = floor(uP[1]);
  for (int poly = 0; poly < 2; poly++) {
    float fp = float(poly);
    for (int e = 0; e < 10; e++) {
      float fe = float(e);
      if (fe >= n) break;
      float t = uTime * uP[0] * 2.0 - fe * uP[2] * 2.0;
      float d = 1e9;
      vec2 first = vertex(fp, 0.0, t, aspect);
      vec2 prev = first;
      for (int v = 1; v < 4; v++) {
        vec2 cur = vertex(fp, float(v), t, aspect);
        d = min(d, segDist(p, prev, cur));
        prev = cur;
      }
      d = min(d, segDist(p, prev, first));
      float fade = 1.0 - fe / n;
      col += palette(fp * 0.5 + uTime * 0.02 + fe * 0.03) * smoothstep(0.004, 0.0, d) * fade * (0.6 + 0.6 * pulse());
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const digitalRain: VisualizationDef = {
  id: 'digital-rain',
  name: 'Digital Rain',
  description:
    'Columns of glowing glyphs pouring down the screen; the heads flare with the bass and the glyphs flicker with the treble.',
  tags: ['energy:mid', 'style:retro'],
  role: 'element',
  params: [
    { id: 'columns', label: 'Columns', type: 'float', min: 20, max: 120, default: 60, step: 1 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0.2, max: 3, default: 1 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0 },
    { id: 'trail', label: 'Trail length', type: 'float', min: 0.1, max: 1, default: 0.45 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float aspect = uResolution.x / uResolution.y;
  float cols = floor(uP[0]);
  float cw = 1.0 / cols;
  float rows = floor(cols / aspect * 1.4);
  vec2 g = vec2(uv.x * cols, (1.0 - uv.y) * rows);
  vec2 cell = floor(g);
  vec2 f = fract(g);
  float speed = (0.3 + 0.7 * hash21(vec2(cell.x, 1.0))) * uP[1];
  float head = fract(uTime * speed * 0.25 + hash21(vec2(cell.x, 2.0))) * (1.0 + uP[3]) * rows;
  float behind = head - cell.y;
  vec3 col = vec3(0.0);
  if (behind >= 0.0 && behind < uP[3] * rows) {
    float fade = 1.0 - behind / (uP[3] * rows);
    // A glyph: 5×7 dots, mirrored for a calligraphic look, changing now and then.
    float glyph = floor(uTime * (1.0 + 6.0 * uTreble) * hash21(cell + 9.0) + hash21(cell));
    vec2 dot_ = floor(vec2(f.x * 5.0, f.y * 7.0));
    vec2 md = vec2(min(dot_.x, 4.0 - dot_.x), dot_.y);
    float on = step(0.5, hash21(md + cell * 0.01 + glyph * 1.7));
    vec2 inDot = fract(vec2(f.x * 5.0, f.y * 7.0)) - 0.5;
    float px = smoothstep(0.5, 0.3, max(abs(inDot.x), abs(inDot.y)));
    vec3 tint = mix(vec3(0.25, 1.0, 0.45), palette(0.5 + 0.3 * hash21(vec2(cell.x, 5.0))), uP[2]);
    float isHead = step(behind, 1.0);
    col = mix(tint, vec3(0.9, 1.0, 0.95), isHead) * on * px * fade * (isHead > 0.5 ? 1.2 + uBass : 0.9);
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const ICON_VISUALIZATIONS: VisualizationDef[] = [
  bouncingLogo,
  morphingSolid,
  trippyMaze,
  meltingSmiley,
  mystify,
  digitalRain,
];
